import {
  createContext,
  type Dispatch,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import type { OverlayMode } from "@/lib/arch-overlay";
import type { ArchitectureData, DrillLevel } from "@/lib/arch-parser";

export type { OverlayMode } from "@/lib/arch-overlay";

export interface ArchVizExtras {
  versionId: string | null;
  commentCounts: Record<string, number>;
  carryOverCounts: Record<string, number>;
  currentUserId: string | null;
  commentsHidden: boolean;
  /** Solution slug — used to link to the version history. Null on shared views. */
  solutionSlug: string | null;
  /** Whether the current viewer can edit (upload new versions, etc.). */
  canWrite: boolean;
  /** Open the New Version dialog. Null when not available (e.g. shared view). */
  onUpload: (() => void) | null;
  /** Open the Share Links dialog. Null when not available. */
  onShare: (() => void) | null;
  /** Open the Media dialog. Null when not available. */
  onMedia: (() => void) | null;
}

export type LayoutMode = "sides" | "compass";

export type ArchVizDialog = "summary" | "legend" | "flows" | "questions" | "versions";

export interface ArchVizState {
  architecture: ArchitectureData | null;
  drillLevel: DrillLevel;
  selectedContainerId: string | null;
  // When set, L3 renders the API surface (permissions + endpoints) of the
  // named external system instead of the components of a container. Mutually
  // exclusive with selectedContainerId — drilling into a container clears
  // this, and drilling into an external clears selectedContainerId.
  selectedExternalId: string | null;
  selectedNodeId: string | null;
  // L3 sub-view for a data-storage container. "components" is the default C4
  // components view; "dataModel" shows the container's business/physical data
  // model. Only meaningful when drillLevel === 3 with a selectedContainerId
  // whose container has a `dataModel`. Reset to "components" on every drill so
  // each entry starts on the familiar view.
  dataView: "components" | "dataModel";
  // Density of the data-model view: "simple" = conceptual entities, "detailed"
  // = physical ERD (field rows + FK edges). Persists across views within a
  // drill; resets with a new architecture.
  dataModelDensity: "simple" | "detailed";
  overlay: OverlayMode;
  activeFlowId: string | null;
  flowStepIndex: number;
  flowPlaying: boolean;
  fullscreen: boolean;
  showLabels: boolean;
  showRisks: boolean;
  risksSheetOpen: boolean;
  /** Increments to signal the DetailPanel to scroll the Risks section into view. */
  scrollToRisksTick: number;
  /** L1 radial arrangement: "sides" (actors above, externals below) or "compass" (actors L, externals R). Per-arch, no persistence. */
  layoutMode: LayoutMode;
  /** Free-text filter applied across the canvas. When non-empty AND no node is
   *  selected/hovered, non-matching nodes dim via the focus choreography. */
  searchQuery: string;
  /** Whether the LevelSummary strip is shown below the breadcrumb. Persisted to localStorage. */
  overviewVisible: boolean;
  /** Which shared dialog is open (summary/legend/flows/questions/versions), or null. */
  openDialog: ArchVizDialog | null;
  /** Scale applied to the canvas's box labels (outer frame, cluster frames, container/component nodes). Persisted to localStorage. */
  labelScale: "small" | "default" | "large";
}

type ArchVizAction =
  | { type: "LOAD"; architecture: ArchitectureData }
  | { type: "CLEAR" }
  | { type: "DRILL_INTO_CONTAINER"; containerId: string }
  | { type: "DRILL_INTO_EXTERNAL"; externalId: string }
  | { type: "DRILL_UP" }
  | { type: "JUMP_TO_LEVEL"; level: DrillLevel }
  | { type: "SELECT_NODE"; nodeId: string | null }
  | { type: "SELECT_NODE_FOR_RISKS"; nodeId: string }
  | { type: "SET_DATA_VIEW"; view: "components" | "dataModel" }
  | { type: "SET_DATA_MODEL_DENSITY"; density: "simple" | "detailed" }
  | { type: "SET_OVERLAY"; overlay: OverlayMode }
  | { type: "START_FLOW"; flowId: string }
  | { type: "STOP_FLOW" }
  | { type: "STEP_FLOW"; delta: number }
  | { type: "SET_FLOW_STEP"; index: number }
  | { type: "TOGGLE_PLAY" }
  | { type: "SET_FULLSCREEN"; value: boolean }
  | { type: "TOGGLE_LABELS" }
  | { type: "TOGGLE_RISKS" }
  | { type: "OPEN_RISKS_SHEET" }
  | { type: "CLOSE_RISKS_SHEET" }
  | { type: "TOGGLE_LAYOUT_MODE" }
  | { type: "SET_SEARCH_QUERY"; query: string }
  | { type: "TOGGLE_OVERVIEW" }
  | { type: "OPEN_DIALOG"; dialog: ArchVizDialog }
  | { type: "CLOSE_DIALOG" }
  | { type: "SET_LABEL_SCALE"; scale: "small" | "default" | "large" };

const initialState: ArchVizState = {
  architecture: null,
  drillLevel: 1,
  selectedContainerId: null,
  selectedExternalId: null,
  selectedNodeId: null,
  dataView: "components",
  dataModelDensity: "simple",
  overlay: "none",
  activeFlowId: null,
  flowStepIndex: 0,
  flowPlaying: false,
  fullscreen: false,
  // Default is the clean view — edge labels surface on hover or when a
  // connected node is selected. Users can opt into the dense "always show"
  // mode via the topbar toggle.
  showLabels: false,
  showRisks: true,
  risksSheetOpen: false,
  scrollToRisksTick: 0,
  // L1 default is "compass" — actors on the left, externals on the right.
  // Resets to "compass" on every page load / new architecture (per-arch
  // exploration affordance, not a user preference).
  layoutMode: "compass",
  searchQuery: "",
  overviewVisible: readOverviewVisible(),
  openDialog: null,
  labelScale: readLabelScale(),
};

const OVERVIEW_VISIBLE_KEY = "archviz:overview-visible";
const LABEL_SCALE_KEY = "archviz:label-scale";

function readOverviewVisible(): boolean {
  if (typeof window === "undefined") return true;
  const raw = window.localStorage.getItem(OVERVIEW_VISIBLE_KEY);
  if (raw === "false") return false;
  if (raw === "true") return true;
  return true;
}

function readLabelScale(): "small" | "default" | "large" {
  if (typeof window === "undefined") return "default";
  const raw = window.localStorage.getItem(LABEL_SCALE_KEY);
  if (raw === "small" || raw === "large") return raw;
  return "default";
}

// Risk-click navigation. When the user clicks a risk in the Level Summary or
// Risks Sheet, the dispatch needs to put the canvas on the level where the
// risk-owning entity is actually visible — otherwise the entity is hidden
// behind a level transition and the canvas looks blank. Rules: navigate to
// the level where the entity is the renderable card, not where it expands
// into its constituents. Externals are cards at L1, containers at L2,
// components at L3-of-their-parent-container. Unknown prefixes (or a missing
// architecture for component lookup) fall through with no navigation change.
export function navigationForRiskTarget(
  nodeId: string,
  architecture: ArchitectureData | null,
): Partial<Pick<ArchVizState, "drillLevel" | "selectedContainerId" | "selectedExternalId">> {
  if (nodeId.startsWith("external:")) {
    return { drillLevel: 1, selectedContainerId: null, selectedExternalId: null };
  }
  if (nodeId.startsWith("container:")) {
    return { drillLevel: 2, selectedContainerId: null, selectedExternalId: null };
  }
  if (nodeId.startsWith("component:") && architecture) {
    const componentId = nodeId.slice("component:".length);
    const parent = architecture.containers.find((c) =>
      c.components.some((cmp) => cmp.id === componentId),
    );
    if (parent) {
      return { drillLevel: 3, selectedContainerId: parent.id, selectedExternalId: null };
    }
  }
  return {};
}

// L3 sub-view default. A data-storage container authors a `dataModel`; when the
// user drills into such a container we open straight on the Data Model view
// rather than making them flip the switch. Non-data containers stay on the
// familiar C4 components view. Mirrors Breadcrumb's `Boolean(container.dataModel)`.
function defaultDataViewFor(
  containerId: string,
  architecture: ArchitectureData | null,
): "components" | "dataModel" {
  const container = architecture?.containers.find((c) => c.id === containerId);
  return container?.dataModel ? "dataModel" : "components";
}

export function reducer(state: ArchVizState, action: ArchVizAction): ArchVizState {
  switch (action.type) {
    case "LOAD":
      return {
        ...initialState,
        architecture: action.architecture,
        showLabels: state.showLabels,
        showRisks: state.showRisks,
        overviewVisible: state.overviewVisible,
        labelScale: state.labelScale,
      };
    case "CLEAR":
      return initialState;
    case "DRILL_INTO_CONTAINER":
      return {
        ...state,
        drillLevel: 3,
        selectedContainerId: action.containerId,
        selectedExternalId: null,
        selectedNodeId: null,
        dataView: defaultDataViewFor(action.containerId, state.architecture),
      };
    case "DRILL_INTO_EXTERNAL":
      return {
        ...state,
        drillLevel: 3,
        selectedContainerId: null,
        selectedExternalId: action.externalId,
        selectedNodeId: null,
      };
    case "DRILL_UP": {
      if (state.risksSheetOpen) {
        return { ...state, risksSheetOpen: false };
      }
      if (state.activeFlowId) {
        return { ...state, activeFlowId: null, flowStepIndex: 0, flowPlaying: false };
      }
      if (state.drillLevel === 3) {
        return {
          ...state,
          drillLevel: 2,
          selectedContainerId: null,
          selectedExternalId: null,
          selectedNodeId: null,
          dataView: "components",
        };
      }
      if (state.drillLevel === 2) {
        return { ...state, drillLevel: 1, selectedNodeId: null };
      }
      return state;
    }
    case "JUMP_TO_LEVEL": {
      if (action.level === 3 && !state.selectedContainerId && !state.selectedExternalId) {
        return state;
      }
      const dataView =
        action.level === 3 && state.selectedContainerId
          ? defaultDataViewFor(state.selectedContainerId, state.architecture)
          : "components";
      return { ...state, drillLevel: action.level, selectedNodeId: null, dataView };
    }
    case "SELECT_NODE":
      return { ...state, selectedNodeId: action.nodeId };
    case "SET_DATA_VIEW":
      return { ...state, dataView: action.view, selectedNodeId: null };
    case "SET_DATA_MODEL_DENSITY":
      return { ...state, dataModelDensity: action.density };
    case "SELECT_NODE_FOR_RISKS":
      return {
        ...state,
        ...navigationForRiskTarget(action.nodeId, state.architecture),
        selectedNodeId: action.nodeId,
        risksSheetOpen: false,
        scrollToRisksTick: state.scrollToRisksTick + 1,
      };
    case "SET_OVERLAY":
      return { ...state, overlay: action.overlay };
    case "TOGGLE_RISKS":
      return { ...state, showRisks: !state.showRisks };
    case "OPEN_RISKS_SHEET":
      return { ...state, risksSheetOpen: true };
    case "CLOSE_RISKS_SHEET":
      return { ...state, risksSheetOpen: false };
    case "START_FLOW":
      return {
        ...state,
        activeFlowId: action.flowId,
        flowStepIndex: 0,
        flowPlaying: true,
        selectedNodeId: null,
      };
    case "STOP_FLOW":
      return { ...state, activeFlowId: null, flowStepIndex: 0, flowPlaying: false };
    case "STEP_FLOW": {
      if (!state.activeFlowId || !state.architecture) return state;
      const flow = state.architecture.dataFlows.find((f) => f.id === state.activeFlowId);
      if (!flow) return state;
      const next = state.flowStepIndex + action.delta;
      const clamped = Math.max(0, Math.min(flow.steps.length - 1, next));
      return { ...state, flowStepIndex: clamped, flowPlaying: false };
    }
    case "SET_FLOW_STEP": {
      if (!state.activeFlowId || !state.architecture) return state;
      const flow = state.architecture.dataFlows.find((f) => f.id === state.activeFlowId);
      if (!flow) return state;
      const clamped = Math.max(0, Math.min(flow.steps.length - 1, action.index));
      return { ...state, flowStepIndex: clamped };
    }
    case "TOGGLE_PLAY":
      return { ...state, flowPlaying: !state.flowPlaying };
    case "SET_FULLSCREEN":
      return { ...state, fullscreen: action.value };
    case "TOGGLE_LABELS":
      return { ...state, showLabels: !state.showLabels };
    case "TOGGLE_LAYOUT_MODE":
      return { ...state, layoutMode: state.layoutMode === "sides" ? "compass" : "sides" };
    case "SET_SEARCH_QUERY":
      return { ...state, searchQuery: action.query };
    case "TOGGLE_OVERVIEW":
      return { ...state, overviewVisible: !state.overviewVisible };
    case "OPEN_DIALOG":
      return { ...state, openDialog: action.dialog };
    case "CLOSE_DIALOG":
      return { ...state, openDialog: null };
    case "SET_LABEL_SCALE":
      return { ...state, labelScale: action.scale };
    default:
      return state;
  }
}

const DEFAULT_EXTRAS: ArchVizExtras = {
  versionId: null,
  commentCounts: {},
  carryOverCounts: {},
  currentUserId: null,
  commentsHidden: false,
  solutionSlug: null,
  canWrite: false,
  onUpload: null,
  onShare: null,
  onMedia: null,
};

const ArchVizContext = createContext<{
  state: ArchVizState;
  dispatch: Dispatch<ArchVizAction>;
  extras: ArchVizExtras;
} | null>(null);

export function ArchVizProvider({
  children,
  extras,
}: {
  children: ReactNode;
  extras?: Partial<ArchVizExtras>;
}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const mergedExtras: ArchVizExtras = useMemo(() => ({ ...DEFAULT_EXTRAS, ...extras }), [extras]);
  const value = useMemo(() => ({ state, dispatch, extras: mergedExtras }), [state, mergedExtras]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(OVERVIEW_VISIBLE_KEY, String(state.overviewVisible));
  }, [state.overviewVisible]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(LABEL_SCALE_KEY, state.labelScale);
  }, [state.labelScale]);

  return <ArchVizContext.Provider value={value}>{children}</ArchVizContext.Provider>;
}

export function useArchViz() {
  const ctx = useContext(ArchVizContext);
  if (!ctx) throw new Error("useArchViz must be used inside ArchVizProvider");
  return ctx;
}
